/**
 * ClassificationAgentPanel
 *
 * Global slide-out panel for the Classification Agent.
 * Renders at the app level and opens when triggered via context.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare, Send, X, Loader2, Bot, User, Sparkles,
  CheckCircle2, AlertCircle, Wrench, ChevronDown, ChevronUp,
  Minimize2, Maximize2,
} from 'lucide-react';
import { useClassificationAgent } from '../../contexts/ClassificationAgentContext';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import './ClassificationAgentPanel.css';

function ClassificationAgentPanel() {
  const { isOpen, context, closeAgent, onResolved } = useClassificationAgent();
  const queryClient = useQueryClient();

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState({ total_cost_usd: 0 });
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [actions, setActions] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (!minimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, minimized]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && sessionId && !minimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, sessionId, minimized]);

  // Initialize session when context changes
  useEffect(() => {
    if (!isOpen || !context) return;

    // Reset state
    setSessionId(null);
    setMessages([]);
    setInput('');
    setError(null);
    setActions([]);
    setUsage({ total_cost_usd: 0 });
    setMinimized(false);

    initSession();
  }, [isOpen, context?.itemId, context?.flagId]);

  const initSession = useCallback(async () => {
    if (!context) return;

    setIsLoading(true);
    setError(null);

    try {
      // Build initial message with context
      const hintText = context.hint || '';
      const initialMessage = `I'm reviewing an item that needs classification help.

**Item Details:**
- ID: ${context.itemId}
- Supplier: ${context.supplier || 'Unknown'}
- Amount: $${Number(context.amount || 0).toLocaleString()}
- Description: "${context.description || ''}"

**Current Classification:**
- Component: ${context.componentName || 'unclassified'}
- Confidence: ${context.confidence ? `${(context.confidence * 100).toFixed(0)}%` : 'N/A'}
- Method: ${context.method || 'unknown'}

${context.categories?.length > 0 ? `**Flag Categories:** ${context.categories.join(', ')}` : ''}

${hintText ? `**Operator Hint:**\n${hintText}` : ''}

Please investigate this classification and help fix it if needed.`;

      const res = await apiClient.post('/api/classification/agent/session', {
        context_type: context.source || 'flag_review',
        item_ids: [context.itemId],
        flag_ids: context.flagIds || (context.flagId ? [context.flagId] : []),
        source_surface: context.source || 'unknown',
        initial_message: initialMessage,
      });

      if (res.ok) {
        setSessionId(res.session.session_id);
        setMessages([
          { role: 'user', content: initialMessage, timestamp: new Date() },
        ]);
        if (res.initial_response) {
          setMessages(m => [...m, {
            role: 'assistant',
            content: res.initial_response.response,
            timestamp: new Date(),
          }]);
          setUsage(res.initial_response.usage || { total_cost_usd: 0 });
        }
        logger.ui.info('AgentPanel', 'session_created', {
          session_id: res.session.session_id,
          item_id: context.itemId,
          source: context.source,
        });
      } else {
        throw new Error(res.error || 'Failed to create session');
      }
    } catch (e) {
      logger.ui.error('AgentPanel', 'init_error', { error: e.message });
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [context]);

  const sendMessage = async () => {
    if (!input.trim() || !sessionId || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setIsLoading(true);
    setError(null);

    try {
      const res = await apiClient.post(`/api/classification/agent/session/${sessionId}/chat`, {
        message: userMessage,
      });

      if (res.ok) {
        setMessages(m => [...m, {
          role: 'assistant',
          content: res.response,
          timestamp: new Date(),
        }]);
        setUsage(res.usage || usage);

        // Check for actions
        const sessionRes = await apiClient.get(`/api/classification/agent/session/${sessionId}`);
        if (sessionRes.ok && sessionRes.actions?.length > 0) {
          setActions(sessionRes.actions);
          // Check if flag was resolved
          const resolveAction = sessionRes.actions.find(a => a.action_type === 'resolve_flag');
          if (resolveAction) {
            onResolved?.();
          }
        }
      } else {
        throw new Error(res.error || 'Failed to send message');
      }
    } catch (e) {
      logger.ui.error('AgentPanel', 'send_error', { error: e.message });
      setError(e.message);
      setMessages(m => [...m, {
        role: 'error',
        content: e.message,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const completeSession = async () => {
    if (sessionId) {
      try {
        await apiClient.post(`/api/classification/agent/session/${sessionId}/complete`);
      } catch (e) {
        // Ignore
      }
    }
    closeAgent();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="cap-backdrop" onClick={completeSession} />
      <div className={`cap-panel ${minimized ? 'cap-panel--minimized' : ''}`}>
        <header className="cap-header">
          <div className="cap-header__title">
            <Bot size={18} />
            <span>Classification Agent</span>
            <span className="cap-header__badge">Claude</span>
          </div>
          <div className="cap-header__meta">
            <span className="cap-header__cost" title="Session cost">
              ${usage.total_cost_usd?.toFixed(4) || '0.0000'}
            </span>
            <button
              className="cap-header__btn"
              onClick={() => setMinimized(m => !m)}
              title={minimized ? 'Expand' : 'Minimize'}
            >
              {minimized ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
            </button>
            <button
              className="cap-header__btn cap-header__btn--close"
              onClick={completeSession}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        {!minimized && (
          <>
            {actions.length > 0 && (
              <div className="cap-actions">
                <button
                  className="cap-actions__toggle"
                  onClick={() => setActionsExpanded(e => !e)}
                >
                  <Wrench size={12} />
                  {actions.length} action{actions.length !== 1 ? 's' : ''} taken
                  {actionsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {actionsExpanded && (
                  <ul className="cap-actions__list">
                    {actions.map((a, i) => (
                      <li key={i} className="cap-actions__item">
                        <CheckCircle2 size={10} />
                        <span className="cap-actions__type">{a.action_type.replace(/_/g, ' ')}</span>
                        <span className="cap-actions__wave">{a.wave_id}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="cap-messages">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`cap-message cap-message--${msg.role}`}
                >
                  <div className="cap-message__icon">
                    {msg.role === 'user' ? <User size={14} /> : msg.role === 'error' ? <AlertCircle size={14} /> : <Bot size={14} />}
                  </div>
                  <div className="cap-message__content">
                    <MessageContent content={msg.content} />
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="cap-message cap-message--assistant cap-message--loading">
                  <div className="cap-message__icon">
                    <Bot size={14} />
                  </div>
                  <div className="cap-message__content">
                    <Loader2 size={14} className="cap-spin" />
                    <span>Thinking...</span>
                  </div>
                </div>
              )}
              {error && !isLoading && (
                <div className="cap-error">
                  <AlertCircle size={14} />
                  {error}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <footer className="cap-input-area">
              <textarea
                ref={inputRef}
                className="cap-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask the agent to investigate, create components, add patterns..."
                rows={2}
                disabled={isLoading || !sessionId}
              />
              <button
                className="cap-send"
                onClick={sendMessage}
                disabled={!input.trim() || isLoading || !sessionId}
                title="Send (Enter)"
              >
                {isLoading ? <Loader2 size={16} className="cap-spin" /> : <Send size={16} />}
              </button>
            </footer>

            <div className="cap-footer">
              <Sparkles size={10} />
              <span>Agent can create components, add patterns, reclassify items, and resolve flags</span>
            </div>
          </>
        )}

        {minimized && (
          <div className="cap-minimized-info">
            {context?.description?.slice(0, 50)}...
          </div>
        )}
      </div>
    </>
  );
}

function MessageContent({ content }) {
  if (!content) return null;

  return (
    <div className="cap-formatted">
      {content.split('\n').map((line, i) => {
        if (line.startsWith('## ')) {
          return <h4 key={i} className="cap-h2">{line.slice(3)}</h4>;
        }
        if (line.startsWith('# ')) {
          return <h3 key={i} className="cap-h1">{line.slice(2)}</h3>;
        }
        if (line.includes('**')) {
          const parts = line.split(/\*\*(.+?)\*\*/g);
          return (
            <p key={i}>
              {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
            </p>
          );
        }
        if (line.match(/^[-*]\s/)) {
          return <li key={i}>{line.slice(2)}</li>;
        }
        if (line.match(/^\d+\.\s/)) {
          return <li key={i}>{line.slice(line.indexOf('.') + 2)}</li>;
        }
        if (line.includes('`')) {
          const parts = line.split(/`(.+?)`/g);
          return (
            <p key={i}>
              {parts.map((part, j) => j % 2 === 1 ? <code key={j}>{part}</code> : part)}
            </p>
          );
        }
        if (line.trim()) {
          return <p key={i}>{line}</p>;
        }
        return null;
      })}
    </div>
  );
}

export default ClassificationAgentPanel;
